import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const ClientCtx = createContext(null);
export const CLIENT_KEY = "revq.client";

export function ClientProvider({ children }) {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientIdState] = useState(() => sessionStorage.getItem(CLIENT_KEY) || "");
  const [confirmed, setConfirmed] = useState(() => !!sessionStorage.getItem(CLIENT_KEY));
  const [realtime, setRealtime] = useState(null); // { bound, client_ids[], scanned_at, last_attempt_at, ok }
  const [pgConn, setPgConn] = useState(null); // { binding|null, available[] }
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Session-scoped: an explicit pick survives refreshes in this tab; cleared on sign-in/out.
  const setSelectedClientId = useCallback((id) => {
    const v = id ? String(id) : "";
    setSelectedClientIdState(v);
    if (v) { sessionStorage.setItem(CLIENT_KEY, v); setConfirmed(true); }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [c, rt, pg] = await Promise.allSettled([
      api.get("/api/edi/note-to-claim/clients"),
      api.get("/api/edi/pg-connection/realtime-status"),
      api.get("/api/edi/pg-connection"),
    ]);
    if (c.status === "fulfilled") {
      const list = c.value.data?.clients || [];
      setClients(list);
      // Keep a still-valid session pick; otherwise default to the API's first client.
      setSelectedClientIdState((cur) => {
        const stillValid = cur && list.some((x) => String(x.client_id) === String(cur));
        if (stillValid) return cur;
        return list[0]?.client_id ? String(list[0].client_id) : "";
      });
    }
    if (rt.status === "fulfilled") setRealtime(rt.value.data || null);
    if (pg.status === "fulfilled") setPgConn(pg.value.data || null);
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const refreshRealtime = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/api/edi/pg-connection/realtime-status/refresh");
      setRealtime(data || null);
      return data;
    } finally {
      setRefreshing(false);
    }
  }, []);

  const isClientRealtime = useCallback(
    (id) => !!(id && realtime?.client_ids && realtime.client_ids.map(String).includes(String(id))),
    [realtime],
  );

  const bound = !!(pgConn?.binding) || !!realtime?.bound;

  const connectionStateOf = useCallback((id) => {
    if (!id) return null;
    if (isClientRealtime(id)) return "connected";
    if (!bound) return "notconnected";
    return "roster";
  }, [isClientRealtime, bound]);

  const connectionState = useMemo(() => connectionStateOf(selectedClientId), [selectedClientId, connectionStateOf]);

  const stored = confirmed ? sessionStorage.getItem(CLIENT_KEY) : null;
  const storedValid = !!stored && clients.some((x) => String(x.client_id) === String(stored));
  const needsChoice = !loading && clients.length > 1 && !storedValid;

  const value = useMemo(
    () => ({
      clients,
      selectedClientId,
      setSelectedClientId,
      realtime,
      pgConn,
      bound,
      loading,
      refreshing,
      refreshRealtime,
      isClientRealtime,
      connectionState,
      connectionStateOf,
      needsChoice,
      reload: loadAll,
    }),
    [clients, selectedClientId, setSelectedClientId, realtime, pgConn, bound, loading, refreshing, refreshRealtime, isClientRealtime, connectionState, connectionStateOf, needsChoice, loadAll],
  );

  return <ClientCtx.Provider value={value}>{children}</ClientCtx.Provider>;
}

export function useClient() {
  const ctx = useContext(ClientCtx);
  if (!ctx) throw new Error("useClient() outside ClientProvider");
  return ctx;
}
